import React from 'react'
import {
  Box,
  Grid,
  Card,
  Typography,
  IconButton,
  Menu,
  MenuItem
} from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { hashHistory } from 'react-router'
import { hasAuthourity } from '../../../../../../../lib/access.es6'
import { formatDateLocale } from '../../../../../../../lib/utils.es6'
import ReinstatePolicy from './ReinstatePolicy'
import ChangeStatusToIssued from './ChangeStatusToIssued'
import CoverLinkQuotationDetail, {
  useCoverLinkStyles,
  getStatusLabel,
  getStatusColor,
  getPrincipalMember
} from './CoverLinkQuotationDetail'

const ITEM_HEIGHT = 48

export default function CoverLinkPolicyDetails({
  policy,
  toggleOpenCancelPolicyDialog,
  setPolicyToDownloadDocumentFor,
  setQuotationToReCreate
}) {
  const classes = useCoverLinkStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [showReinstateDialog, setShowReinstateDialog] = React.useState(false)
  const [showChangeStatusDialog, setShowChangeStatusDialog] = React.useState(
    false
  )
  const open = Boolean(anchorEl)

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const isCommercialPolicy =
    policy.policy_number && policy.policy_number.startsWith('CIC-COM')
  const isDomesticPolicy =
    policy.product_code === 'domestic_cover' ||
    (policy.policy_number && policy.policy_number.startsWith('CIC-DOM'))

  const principalMember = getPrincipalMember(policy.quotation)

  return (
    <Card className={classes.mainCard} style={{ marginTop: '20px' }}>
      {/* Header Section */}
      <Box className={classes.headerSection}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography className={classes.policyNumber}>
              Policy No: <strong>{policy.policy_number || 'N/A'}</strong> |
              Policy Start:{' '}
              <strong>{formatDateLocale(policy.start_date) || 'N/A'}</strong>
            </Typography>
            <Box
              className={`${classes.statusBadge} ${
                policy.issued
                  ? classes.statusSuccess
                  : isCommercialPolicy || isDomesticPolicy
                  ? {
                      success: classes.statusSuccess,
                      warning: classes.statusWarning,
                      error: classes.statusError,
                      default: classes.statusDefault
                    }[getStatusColor(policy.status)]
                  : policy.status === 'Active'
                  ? classes.statusSuccess
                  : classes.statusDefault
              }`}
            >
              {policy.issued
                ? 'Issued'
                : isCommercialPolicy || isDomesticPolicy
                ? getStatusLabel(policy.status)
                : policy.status}
            </Box>
          </Grid>
          <Grid item>
            {principalMember && (
              <Box className={classes.principalInfo}>
                <Typography style={{ fontWeight: 'bold', fontSize: '12px' }}>
                  {principalMember.dependant.first_name}{' '}
                  {principalMember.dependant.last_name}
                </Typography>
                <Typography style={{ fontSize: '11px' }}>
                  ID: {principalMember.dependant.national_id}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item>
            <IconButton
              className={classes.actionButton}
              onClick={handleClick}
              size="small"
            >
              <MoreVertIcon style={{ color: 'white' }} />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Content Section */}
      <Box className={classes.contentWrapper}>
        <CoverLinkQuotationDetail quotation={policy.quotation} meta={policy} />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5,
            width: '20ch'
          }
        }}
      >
        {![
          'Non_Payment',
          'Cancelled',
          'Discarded',
          'Expired',
          'Not_Taken_Up'
        ].includes(policy.status) && (
          <MenuItem
            onClick={() => {
              setQuotationToReCreate && setQuotationToReCreate(policy)
              hashHistory.push(`admin/generate-policy/${policy.policy_number}`)
              handleClose()
            }}
            style={{
              cursor: 'pointer',
              fontFamily: 'Open Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '15px',
              color: '#000000'
            }}
          >
            Amend
          </MenuItem>
        )}
        {['Unpaid', 'Un_confirmed'].includes(policy.status) &&
          !policy.issued &&
          hasAuthourity('CAPTURE_POLICY') && (
            <MenuItem
              onClick={() => {
                setShowChangeStatusDialog(true)
                handleClose()
              }}
              style={{
                cursor: 'pointer',
                fontFamily: 'Open Sans',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '15px',
                color: '#000000'
              }}
            >
              Change Status to Issued
            </MenuItem>
          )}
        {!['Non_Payment', 'Cancelled', 'Rejected', 'Un_Confirmed'].includes(
          policy.status
        ) && (
          <MenuItem
            onClick={() => {
              toggleOpenCancelPolicyDialog && toggleOpenCancelPolicyDialog()
              handleClose()
            }}
            style={{
              cursor: 'pointer',
              fontFamily: 'Open Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '15px',
              color: '#000000',
              display: hasAuthourity('CAPTURE_POLICY') ? 'block' : 'none'
            }}
          >
            Cancel Policy
          </MenuItem>
        )}
        {policy.status === 'Cancelled' && (
          <MenuItem
            onClick={() => {
              setShowReinstateDialog(true)
              handleClose()
            }}
            style={{
              cursor: 'pointer',
              fontFamily: 'Open Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '15px',
              color: '#000000'
            }}
          >
            Reinstate Policy
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setPolicyToDownloadDocumentFor &&
              setPolicyToDownloadDocumentFor(policy)
            handleClose()
          }}
          style={{
            cursor: 'pointer',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '15px',
            color: '#000000'
          }}
        >
          Download Documents
        </MenuItem>
      </Menu>

      <ReinstatePolicy
        open={showReinstateDialog}
        onClose={() => setShowReinstateDialog(false)}
        policy={policy}
        onSuccess={() => {
          setTimeout(() => {
            window.location.reload()
          }, 1500)
        }}
      />
      <ChangeStatusToIssued
        open={showChangeStatusDialog}
        onClose={() => setShowChangeStatusDialog(false)}
        policy={policy}
        onSuccess={() => {
          setTimeout(() => {
            window.location.reload()
          }, 1500)
        }}
      />
    </Card>
  )
}
